import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaBars, FaTimes } from 'react-icons/fa';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Handle scroll event to change navbar appearance
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 60) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when changing routes
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Products', path: '/products' },
    { name: 'Contact', path: '/contact' }
  ];

  // Navigation bar styles based on scroll position
  const navbarClasses = `fixed top-0 left-0 w-full z-50 transition-all duration-500 ease-in-out ${
    scrolled 
      ? 'bg-white shadow-lg py-3' 
      : 'bg-white/80 backdrop-blur-md py-5'
  }`;

  // Logo text styles
  const logoTextClasses = `font-heading font-bold text-xl md:text-2xl`;
  
  return (
    <nav className={navbarClasses}>
      <div className="container-custom flex justify-between items-center">
        {/* Logo */}
        <Link to="/" className="flex items-center z-10">
          <div className="flex items-center justify-center px-2">
            {/* Use company logo with fallback */}
            <img 
              src={`${process.env.PUBLIC_URL}/product_img/MIGLIORE Logo png.png`}
              alt="Migliore Life Sciences" 
              className={`object-contain transition-all duration-500 ease-in-out ${scrolled ? 'h-16' : 'h-20'}`}
              onError={(e) => {
                console.error("Primary logo failed to load");
                e.target.onerror = null; // Prevent infinite loop
                e.target.src = "/logo-white.png"; // Fallback to logo-white.png
              }}
            />
          </div>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center space-x-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`font-medium transition-colors duration-300 ease-in-out ${
                location.pathname === link.path
                  ? 'text-primary font-semibold'
                  : 'text-gray-700 hover:text-primary-dark'
              }`}
            >
              {link.name}
            </Link>
          ))}
          <Link 
            to="/contact" 
            className="btn-primary"
          >
            Contact Us
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden z-10 focus:outline-none"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? (
            <FaTimes className="h-6 w-6 text-primary" />
          ) : (
            <FaBars className="h-6 w-6 text-primary" />
          )}
        </button>

        {/* Mobile Navigation */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-lg py-4 px-6 flex flex-col space-y-4"
          >
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-medium py-2 transition-colors duration-300 ease-in-out ${
                  location.pathname === link.path
                    ? 'text-primary font-semibold'
                    : 'text-gray-700 hover:text-primary-dark'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link 
              to="/contact" 
              className="btn-primary text-center"
            >
              Contact Us
            </Link>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
